import React from 'react';
import { AnimatePresence } from 'framer-motion';
import '../styles/index.css';
import useGraderFlow from '../hooks/useGraderFlow';
import SearchSection from './SearchSection';
import ScanningPhase from './ScanningPhase';
import PreviewPhase from './PreviewPhase';
import UnlockPhase from './UnlockPhase';
import FullReportPhase from './FullReportPhase';
import ErrorPhase from './ErrorPhase';

const GraderShell = () => {
  const {
    query,
    suggestions,
    showDropdown,
    setShowDropdown,
    loadingSuggestions,
    searchError,
    phase,
    scanStepIndex,
    selectedBusiness,
    preview,
    fullReport,
    unlocking,
    unlockStep,
    unlockPhone,
    setUnlockPhone,
    otpCode,
    setOtpCode,
    otpStatusMessage,
    otpResendCooldownMs,
    unlockError,
    otpSending,
    otpChecking,
    prospectSubmitting,
    prospectName,
    setProspectName,
    prospectEmail,
    setProspectEmail,
    prospectRelationship,
    setProspectRelationship,
    prospectOptIn,
    setProspectOptIn,
    prospectConsentAccepted,
    setProspectConsentAccepted,
    scanTelemetry,
    isCompactMobile,
    showScanDetails,
    setShowScanDetails,
    previewRevealStage,
    wrapperRef,
    handleSearchChange,
    onSubmit,
    onSelectSuggestion,
    currentScanStepLabel,
    scanProgress,
    mapEmbedUrl,
    mapZoomTransform,
    visibleScanActivity,
    scanConfidencePct,
    scanActivity,
    pillarCards,
    beginUnlockFlow,
    handleSendOtp,
    otpCooldownSeconds,
    handleVerifyOtp,
    handleSubmitProspect,
    atRiskPillars,
    winningPillars,
    resetAll,
    SCAN_STEPS,
    pendingBootstrapBusiness,
    pendingBootstrapError,
    confirmPendingBootstrap,
    cancelPendingBootstrap
  } = useGraderFlow();

  return (
    <section className={`interactive-demo interactiveDemoWrap${isCompactMobile ? ' is-mobile-ui' : ''}`}>
      <div className="container">
        <div className="demo-header">
          <h2 className="demo-title">Get Your Free Online Visibility Scan</h2>
          <p className="demo-subtitle">Discover where competitors are beating you on Google and what to fix first.</p>
        </div>

        <div className="demo-box interactiveDemoGlass">
          <SearchSection
            wrapperRef={wrapperRef}
            onSubmit={onSubmit}
            query={query}
            handleSearchChange={handleSearchChange}
            suggestions={suggestions}
            loadingSuggestions={loadingSuggestions}
            setShowDropdown={setShowDropdown}
            resetAll={resetAll}
            showDropdown={showDropdown}
            onSelectSuggestion={onSelectSuggestion}
            searchError={searchError}
          />

          {pendingBootstrapBusiness && phase === 'search' && (
            <div className="pending-bootstrap-card" role="dialog" aria-label="Confirm scan">
              <div className="pending-bootstrap-card__copy">
                <strong>Run free visibility scan for:</strong>
                <span className="pending-bootstrap-card__name">{pendingBootstrapBusiness.name}</span>
                {pendingBootstrapBusiness.city && (
                  <span className="pending-bootstrap-card__meta">{pendingBootstrapBusiness.city}</span>
                )}
              </div>
              <div className="pending-bootstrap-card__actions">
                <button type="button" className="btn-try-again" onClick={confirmPendingBootstrap}>
                  Run scan
                </button>
                <button type="button" className="btn-try-again secondary" onClick={cancelPendingBootstrap}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {pendingBootstrapError && phase === 'search' && !pendingBootstrapBusiness && (
            <p className="booking-note booking-error">{pendingBootstrapError}</p>
          )}

          <AnimatePresence mode="wait">
            {phase === 'scanning' && (
              <ScanningPhase
                selectedBusiness={selectedBusiness}
                isCompactMobile={isCompactMobile}
                currentScanStepLabel={currentScanStepLabel}
                scanProgress={scanProgress}
                visibleScanActivity={visibleScanActivity}
                scanConfidencePct={scanConfidencePct}
                mapEmbedUrl={mapEmbedUrl}
                mapZoomTransform={mapZoomTransform}
                SCAN_STEPS={SCAN_STEPS}
                scanStepIndex={scanStepIndex}
                showScanDetails={showScanDetails}
                setShowScanDetails={setShowScanDetails}
                scanTelemetry={scanTelemetry}
                scanActivity={scanActivity}
              />
            )}

            {phase === 'preview' && preview && (
              <PreviewPhase
                preview={preview}
                previewRevealStage={previewRevealStage}
                pillarCards={pillarCards}
                beginUnlockFlow={beginUnlockFlow}
              />
            )}

            {phase === 'booking' && (
              <UnlockPhase
                unlockStep={unlockStep}
                unlockPhone={unlockPhone}
                setUnlockPhone={setUnlockPhone}
                handleSendOtp={handleSendOtp}
                otpSending={otpSending}
                otpResendCooldownMs={otpResendCooldownMs}
                otpCooldownSeconds={otpCooldownSeconds}
                otpStatusMessage={otpStatusMessage}
                otpCode={otpCode}
                setOtpCode={setOtpCode}
                handleVerifyOtp={handleVerifyOtp}
                otpChecking={otpChecking}
                prospectName={prospectName}
                setProspectName={setProspectName}
                prospectEmail={prospectEmail}
                setProspectEmail={setProspectEmail}
                prospectRelationship={prospectRelationship}
                setProspectRelationship={setProspectRelationship}
                prospectOptIn={prospectOptIn}
                setProspectOptIn={setProspectOptIn}
                prospectConsentAccepted={prospectConsentAccepted}
                setProspectConsentAccepted={setProspectConsentAccepted}
                handleSubmitProspect={handleSubmitProspect}
                prospectSubmitting={prospectSubmitting}
                unlocking={unlocking}
                unlockError={unlockError}
              />
            )}

            {phase === 'full' && fullReport && (
              <FullReportPhase
                fullReport={fullReport}
                atRiskPillars={atRiskPillars}
                winningPillars={winningPillars}
                resetAll={resetAll}
              />
            )}

            {phase === 'error' && (
              <ErrorPhase searchError={searchError} resetAll={resetAll} />
            )}
          </AnimatePresence>
        </div>
      </div>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </section>
  );
};

export default GraderShell;
